import { AlertSummary } from "../../../../models/Alerts/Alert";

// Every open tab shows the same alerts badge, so only one of them needs to
// hit /summary per poll interval. The tab that fetches writes the result to
// localStorage; the `storage` event delivers it to every other tab on the
// origin, and a tab that wakes up reads it back instead of re-fetching while
// it is still fresh. No leader election: whichever tab is due fetches next.
const STORAGE_KEY = "commander:alerts-summary";
// Held while a tab fetches so two visible tabs can't poll at the same moment.
const POLL_LOCK_NAME = `${STORAGE_KEY}:poll-lock`;

export interface SharedAlertsSummary {
  summary: AlertSummary;
  fetchedAt: number;
}

export const readSharedSummary = (): SharedAlertsSummary | null => {
  try {
    return parse(localStorage.getItem(STORAGE_KEY));
  } catch (error) {
    return null;
  }
};

export const writeSharedSummary = (entry: SharedAlertsSummary): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch (error) {
    // Storage full or blocked: this tab still has its own copy.
  }
};

/** Called on logout so the next admin never sees the previous one's counts. */
export const clearSharedSummary = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // ignore
  }
};

/** Fires when another tab publishes a summary. Returns the unsubscribe. */
export const subscribeSharedSummary = (
  listener: (entry: SharedAlertsSummary) => void,
): (() => void) => {
  const handler = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;
    const entry = parse(event.newValue);
    if (entry) listener(entry);
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
};

/**
 * Runs `fn` under a cross-tab lock. With `wait` false it resolves to null
 * when another tab already holds the lock (its result will arrive via the
 * storage event). Without the Web Locks API it just runs `fn`.
 */
export const withPollLock = async <T>(
  wait: boolean,
  fn: () => Promise<T>,
): Promise<T | null> => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return fn();
  if (wait) return locks.request(POLL_LOCK_NAME, fn);
  return locks.request(POLL_LOCK_NAME, { ifAvailable: true }, (lock) =>
    lock ? fn() : null,
  );
};

const parse = (raw: string | null): SharedAlertsSummary | null => {
  if (!raw) return null;
  try {
    const entry = JSON.parse(raw) as Partial<SharedAlertsSummary>;
    if (
      typeof entry?.fetchedAt !== "number" ||
      !entry.summary ||
      !Array.isArray(entry.summary.sections)
    ) {
      return null;
    }
    return entry as SharedAlertsSummary;
  } catch (error) {
    return null;
  }
};
